import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import type { Theme } from '../contexts/ThemeContext';
import { storage } from '../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { UserService } from '@/services/UserService';
import { resolveType } from '@/services/ServiceContainer';


// Compact loading spinner component
const CompactLoadingSpinner: React.FC<{ size: number }> = ({ size }) => {
  const { theme } = useTheme();
  const spinnerSize = size * 0.4;

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg
        width={spinnerSize}
        height={spinnerSize}
        viewBox="0 0 50 50"
        style={{
          animation: 'rotate 1s linear infinite',
        }}
      >
        <circle
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke={theme.colors.PRIMARY}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray="80, 200"
          strokeDashoffset="0"
          style={{
            animation: 'dash 1.5s ease-in-out infinite',
          }}
        />
      </svg>
      <style>
        {`
          @keyframes rotate {
            100% {
              transform: rotate(360deg);
            }
          }
          @keyframes dash {
            0% {
              stroke-dasharray: 1, 200;
              stroke-dashoffset: 0;
            }
            50% {
              stroke-dasharray: 89, 200;
              stroke-dashoffset: -35px;
            }
            100% {
              stroke-dasharray: 89, 200;
              stroke-dashoffset: -124px;
            }
          }
        `}
      </style>
    </div>
  );
};

// SVG icons as components
const UploadIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 14.9861C11 15.5384 11.4477 15.9861 12 15.9861C12.5523 15.9861 13 15.5384 13 14.9861V7.82831L16.2428 11.0711C16.6333 11.4616 17.2665 11.4616 17.657 11.0711C18.0475 10.6806 18.0475 10.0474 17.657 9.65692L12.7071 4.70703C12.3166 4.31651 11.6834 4.31651 11.2929 4.70703L6.34315 9.65692C5.95262 10.0474 5.95262 10.6806 6.34315 11.0711C6.73367 11.4616 7.36684 11.4616 7.75736 11.0711L11 7.82831V14.9861Z" fill={color} />
    <path d="M4 14C4 13.4477 3.55228 13 3 13C2.44772 13 2 13.4477 2 14V16C2 18.2091 3.79086 20 6 20H18C20.2091 20 22 18.2091 22 16V14C22 13.4477 21.5523 13 21 13C20.4477 13 20 13.4477 20 14V16C20 17.1046 19.1046 18 18 18H6C4.89543 18 4 17.1046 4 16V14Z" fill={color} />
  </svg>
);

const CameraIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 8.5C8.5 7.67157 9.17157 7 10 7H14C14.8284 7 15.5 7.67157 15.5 8.5V9.5H8.5V8.5Z" fill={color} />
    <path fillRule="evenodd" clipRule="evenodd" d="M6.5 6C4.567 6 3 7.567 3 9.5V16.5C3 18.433 4.567 20 6.5 20H17.5C19.433 20 21 18.433 21 16.5V9.5C21 7.567 19.433 6 17.5 6H16.5C16.5 4.067 14.933 2.5 13 2.5H11C9.067 2.5 7.5 4.067 7.5 6H6.5ZM12 17C13.933 17 15.5 15.433 15.5 13.5C15.5 11.567 13.933 10 12 10C10.067 10 8.5 11.567 8.5 13.5C8.5 15.433 10.067 17 12 17Z" fill={color} />
  </svg>
);

const ResetIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C15.0736 20 17.7196 18.3745 19 15.9297" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M12 8V12L15 14" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 8V4M19 4H15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AvatarIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="8" r="4" stroke={color} strokeWidth="2" />
    <path d="M5 19C5 16.2386 7.23858 14 10 14H14C16.7614 14 19 16.2386 19 19" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

interface ProfilePictureProps {
  size?: number;
  editable?: boolean;
}

interface CameraModalProps {
  onClose: () => void;
  onCapture: (file: File) => void;
}

const getModalStyles = (): React.CSSProperties => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
});

const getContentStyles = (theme: Theme): React.CSSProperties => ({
  backgroundColor: theme.colors.SURFACE,
  padding: '20px',
  borderRadius: '8px',
  maxWidth: '90vmin',
  maxHeight: '90vmin',
  width: '90vmin',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '10px',
  position: 'relative'
});

const CameraModal: React.FC<CameraModalProps> = ({ onClose, onCapture }) => {
  const { theme } = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5
  });

  useEffect(() => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          streamRef.current = stream;
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
        setError('Error accessing camera');
        onClose();
      }
    };

    startCamera();
    return () => {
      stopCamera();
    };
  }, [onClose]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  };

  const handleCapture = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg');
        setCapturedImage(dataUrl);

        // Calculate the shortest dimension and convert to percentage
        const shortestDimension = Math.min(canvas.width, canvas.height);
        const cropSizePixels = shortestDimension * 0.9; // 90% of shortest dimension

        // Convert pixel values to percentages
        const cropSizePercentageWidth = (cropSizePixels / canvas.width) * 100;
        const cropSizePercentageHeight = (cropSizePixels / canvas.height) * 100;

        // Center the crop
        const x = (100 - cropSizePercentageWidth) / 2;
        const y = (100 - cropSizePercentageHeight) / 2;

        setCrop({
          unit: '%',
          width: cropSizePercentageWidth,
          height: cropSizePercentageHeight,
          x,
          y
        });

        setShowCropper(true);
        stopCamera();
      }
    }
  };

  const handleCrop = async () => {
    if (!capturedImage) return;

    const image = new Image();
    image.src = capturedImage;

    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Calculate actual pixel values from percentage
    const scaleX = image.naturalWidth / 100;
    const scaleY = image.naturalHeight / 100;
    const pixelCrop = {
      x: Math.round(crop.x * scaleX),
      y: Math.round(crop.y * scaleY),
      width: Math.round(crop.width * scaleX),
      height: Math.round(crop.height * scaleY)
    };

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
        onCapture(file);
      }
    }, 'image/jpeg', 0.95);
  };

  const modalStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const contentStyle: React.CSSProperties = {
    backgroundColor: theme.colors.SURFACE,
    padding: '20px',
    borderRadius: '8px',
    maxWidth: '90vmin',
    maxHeight: '90vmin',
    width: '90vmin',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    position: 'relative'
  };

  return (
    <div style={modalStyle}>
      <div style={{
        ...contentStyle,
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}>
        {error && (
          <div style={{
            backgroundColor: `${theme.colors.ERROR}20`,
            color: theme.colors.ERROR,
            padding: theme.spacing.MEDIUM,
            borderRadius: '4px',
            marginBottom: theme.spacing.MEDIUM,
            textAlign: 'center',
            width: '100%',
            fontWeight: 'bold'
          }}>
            {error}
          </div>
        )}
        {!showCropper ? (
          <>
            <div style={{
              width: '100%',
              flex: '1',
              minHeight: 0,
              position: 'relative',
              marginBottom: '20px'
            }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: '4px'
                }}
              />
            </div>
            <button
              style={{
                padding: '10px 20px',
                backgroundColor: theme.colors.PRIMARY,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                width: '100%',
                marginTop: 'auto'
              }}
              onClick={handleCapture}
            >
              Capture
            </button>
          </>
        ) : (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {capturedImage && (
              <>
                <div style={{ width: '100%', position: 'relative', maxHeight: '70vh', overflow: 'hidden' }}>
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    aspect={1}
                    circularCrop
                    minWidth={50}
                    minHeight={50}
                    ruleOfThirds
                  >
                    <img
                      src={capturedImage}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        maxHeight: '60vh'
                      }}
                      alt="Captured"
                    />
                  </ReactCrop>
                  <div style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'center',
                    marginTop: '10px'
                  }}>
                    <button
                      style={{
                        padding: '10px 20px',
                        backgroundColor: theme.colors.PRIMARY,
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                      onClick={handleCrop}
                    >
                      Save
                    </button>
                    <button
                      style={{
                        padding: '10px 20px',
                        backgroundColor: theme.colors.ERROR,
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Define color sets for avatars - each set has light and dark theme variants
const AVATAR_COLOR_SETS = [
  { // Green
    light: { bg: '#2C5530', text: '#FFFFFF' },
    dark: { bg: '#4A7558', text: '#FFFFFF' }
  },
  { // Blue
    light: { bg: '#1E4D8C', text: '#FFFFFF' },
    dark: { bg: '#3A6FB1', text: '#FFFFFF' }
  },
  { // Purple
    light: { bg: '#5B2E91', text: '#FFFFFF' },
    dark: { bg: '#7B4EAF', text: '#FFFFFF' }
  },
  { // Red
    light: { bg: '#A13232', text: '#FFFFFF' },
    dark: { bg: '#C45A5A', text: '#FFFFFF' }
  },
  { // Teal
    light: { bg: '#206F6F', text: '#FFFFFF' },
    dark: { bg: '#3D9B9B', text: '#FFFFFF' }
  },
  { // Orange
    light: { bg: '#B54E00', text: '#FFFFFF' },
    dark: { bg: '#E66300', text: '#FFFFFF' }
  },
  { // Pink
    light: { bg: '#9C2766', text: '#FFFFFF' },
    dark: { bg: '#D44B93', text: '#FFFFFF' }
  },
  { // Brown
    light: { bg: '#6F4E37', text: '#FFFFFF' },
    dark: { bg: '#9B7259', text: '#FFFFFF' }
  },
  { // Slate
    light: { bg: '#445469', text: '#FFFFFF' },
    dark: { bg: '#687D99', text: '#FFFFFF' }
  },
  { // Indigo
    light: { bg: '#3C366B', text: '#FFFFFF' },
    dark: { bg: '#5A5399', text: '#FFFFFF' }
  },
];

const ProfilePicture: React.FC<ProfilePictureProps> = ({
  size = 100,
  editable = true,
}) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [profileUrl, setProfileUrl] = useState<string | null>(null);
  const [isCustomPhoto, setIsCustomPhoto] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [uploadImage, setUploadImage] = useState<string | null>(null);
  const [uploadCrop, setUploadCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5
  });

  useEffect(() => {
    const fetchProfileUrl = async () => {
      if (!user) return;

      try {
        const userService = resolveType(UserService);
        const userModel = await userService.get(user.uid);
        if (userModel) {
          setProfileUrl(userModel.photoURL || null);
          setIsCustomPhoto(true);
        } else if (user.photoURL) {
          setProfileUrl(user.photoURL);
          setIsCustomPhoto(false);
        }

      } catch (err) {
        console.error('Error fetching profile picture:', err);
        setError('Error fetching profile picture');
      }
    };

    fetchProfileUrl();
  }, [user]);

  // Get user's initials from display name or email
  const getInitials = () => {
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .map((name) => name[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.[0].toUpperCase() || '?';
  };

  // Get consistent color set based on user ID
  const getAvatarColors = () => {
    const str = user?.uid || user?.email || '';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % AVATAR_COLOR_SETS.length;
    return theme.isDark ? AVATAR_COLOR_SETS[index].dark : AVATAR_COLOR_SETS[index].light;
  };

  const handleImageClick = () => {
    if (editable) {
      setShowOptions(true);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        setUploadImage(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        setUploadImage(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const img = new Image();
      const imageUrl = URL.createObjectURL(file);
      img.src = imageUrl;

      img.onload = () => {
        setError(''); // Clear any previous errors
        // Calculate the shortest dimension and convert to percentage
        const shortestDimension = Math.min(img.width, img.height);
        const cropSizePixels = shortestDimension * 0.9; // 90% of shortest dimension

        // Convert pixel values to percentages
        const cropSizePercentageWidth = (cropSizePixels / img.width) * 100;
        const cropSizePercentageHeight = (cropSizePixels / img.height) * 100;

        // Center the crop
        const x = (100 - cropSizePercentageWidth) / 2;
        const y = (100 - cropSizePercentageHeight) / 2;

        setUploadCrop({
          unit: '%',
          width: cropSizePercentageWidth,
          height: cropSizePercentageHeight,
          x,
          y
        });
        setUploadImage(imageUrl);
      };
    }
    // Reset the input value so the same file can be selected again
    e.target.value = '';
  };

  const handleOptionClick = (option: 'upload' | 'camera') => {
    setShowOptions(false);
    if (option === 'upload' && fileInputRef.current) {
      fileInputRef.current.click();
    } else if (option === 'camera') {
      setShowCamera(true);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!user) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Create a unique filename using timestamp
      const timestamp = Date.now();
      const filename = `${timestamp}_${file.name}`;

      // Upload to Firebase Storage
      const storageRef = ref(
        storage,
        `profile-pictures/${user.uid}/${filename}`
      );
      await uploadBytes(storageRef, file);

      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update Firestore with the new profile picture URL
      const userService = resolveType(UserService);
      await userService.update(user.uid, {
        photoURL: downloadURL,
      });

      // Update local state instead of reloading
      setProfileUrl(downloadURL);
      setIsCustomPhoto(true);
    } catch (err) {
      console.error('Failed to update profile picture:', err);
      setError('Failed to update profile picture');
    } finally {
      setLoading(false);
      setShowOptions(false);  // Close the menu after operation completes
    }
  };

  const handleResetToProviderPhoto = async () => {
    if (!user || !user.photoURL) return;

    try {
      setLoading(true);
      setError('');

      // Remove the profilePictureUrl from Firestore
      const userService = resolveType(UserService);
      await userService.update(user.uid, {
        photoURL: null,
      });

      // Update local state to show provider photo
      setProfileUrl(user.photoURL);
      setIsCustomPhoto(false);
    } catch (err) {
      console.error('Failed to reset profile picture:', err);
      setError('Failed to reset profile picture');
    } finally {
      setLoading(false);
      setShowOptions(false);  // Close the menu after operation completes
    }
  };

  const handleUseAvatar = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError('');

      // Store a special URL in Firestore to indicate avatar mode
      // Using a special URL format that can be recognized across the app
      const avatarUrl = `avatar://${user.uid}`;
      const userService = resolveType(UserService);
      await userService.update(user.uid, {
        photoURL: avatarUrl,
      });

      // Update local state to show initials
      setProfileUrl(avatarUrl);
      setIsCustomPhoto(true);
    } catch (err) {
      console.error('Failed to set avatar:', err);
      setError('Failed to set avatar');
    } finally {
      setLoading(false);
      setShowOptions(false);  // Close the menu after operation completes
    }
  };

  const handleUploadCrop = async () => {
    if (!uploadImage) return;

    const image = new Image();
    image.src = uploadImage;

    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Calculate actual pixel values from percentage
    const scaleX = image.naturalWidth / 100;
    const scaleY = image.naturalHeight / 100;
    const pixelCrop = {
      x: Math.round(uploadCrop.x * scaleX),
      y: Math.round(uploadCrop.y * scaleY),
      width: Math.round(uploadCrop.width * scaleX),
      height: Math.round(uploadCrop.height * scaleY)
    };

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
        handleFileUpload(file);
        setUploadImage(null);
        URL.revokeObjectURL(uploadImage);
      }
    }, 'image/jpeg', 0.95);
  };

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: size,
    height: size,
    borderRadius: '50%',
    overflow: 'hidden',
    cursor: editable ? 'pointer' : 'default',
    border: `2px solid ${theme.colors.BORDER}`,
    backgroundColor: profileUrl && !profileUrl.startsWith('avatar://') ? 'transparent' : getAvatarColors().bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: profileUrl && !profileUrl.startsWith('avatar://') ? '#FFFFFF' : getAvatarColors().text,
    fontSize: `${size * 0.4}px`,
    fontWeight: 'bold',
    textShadow: theme.isDark ?
      '0 1px 2px rgba(0,0,0,0.5)' : // Stronger shadow in dark mode
      '0 1px 1px rgba(0,0,0,0.3)', // Lighter shadow in light mode
  };

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    color: '#FFFFFF',
    opacity: isHovered ? 1 : 0,
    transition: 'opacity 0.2s',
    fontSize: `${size * 0.14}px`,
    textAlign: 'center',
    padding: `${size * 0.1}px`,
    lineHeight: 1.2,
    fontWeight: '500',
  };

  const optionsStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginTop: theme.spacing.SMALL,
    backgroundColor: theme.colors.SURFACE,
    border: `1px solid ${theme.colors.BORDER}`,
    borderRadius: '4px',
    padding: theme.spacing.SMALL,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.SMALL,
    zIndex: 1000,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    minWidth: size * 1.5,
  };

  const optionButtonStyle: React.CSSProperties = {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: theme.colors.TEXT,
    cursor: 'pointer',
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    transition: 'background-color 0.2s',
  };

  const optionButtonHoverStyle: React.CSSProperties = {
    ...optionButtonStyle,
    backgroundColor: theme.colors.BORDER,
  };

  return (
    <>
      <div style={{
        position: 'relative',
        display: 'inline-block',
        verticalAlign: 'middle',
      }}>
        <div
          style={containerStyle}
          onClick={handleImageClick}
          onMouseEnter={() => editable && setIsHovered(true)}
          onMouseLeave={() => {
            setIsHovered(false);
            if (!showOptions) {
              setTimeout(() => setShowOptions(false), 300);
            }
          }}
        >
          {loading ? (
            <CompactLoadingSpinner size={size} />
          ) : profileUrl?.startsWith('avatar://') ? (
            <span>{getInitials()}</span>
          ) : profileUrl ? (
            <img src={profileUrl} alt="Profile" style={imageStyle} />
          ) : (
            <span>{getInitials()}</span>
          )}
          {editable && !loading && (
            <div style={overlayStyle}>
              Change Pic
            </div>
          )}
        </div>
        {showOptions && (
          <div
            style={{
              ...optionsStyle,
              left: '100%',
              top: '0',
              transform: 'translateX(8px)',
              marginTop: 0,
              minWidth: Math.max(size * 2, 180),
            }}
            onMouseEnter={() => setShowOptions(true)}
            onMouseLeave={() => setShowOptions(false)}
          >
            <button
              style={optionButtonStyle}
              onClick={() => handleOptionClick('upload')}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, optionButtonHoverStyle);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.currentTarget.style, optionButtonStyle);
              }}
            >
              <UploadIcon color={theme.colors.TEXT} /> Upload Pic
            </button>
            <button
              style={optionButtonStyle}
              onClick={() => handleOptionClick('camera')}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, optionButtonHoverStyle);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.currentTarget.style, optionButtonStyle);
              }}
            >
              <CameraIcon color={theme.colors.TEXT} /> Take Pic
            </button>
            <button
              style={optionButtonStyle}
              onClick={handleUseAvatar}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, optionButtonHoverStyle);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.currentTarget.style, optionButtonStyle);
              }}
              disabled={loading}
            >
              <AvatarIcon color={theme.colors.TEXT} /> Use Avatar
            </button>
            {isCustomPhoto && user?.photoURL && (
              <button
                style={optionButtonStyle}
                onClick={handleResetToProviderPhoto}
                onMouseEnter={(e) => {
                  Object.assign(e.currentTarget.style, optionButtonHoverStyle);
                }}
                onMouseLeave={(e) => {
                  Object.assign(e.currentTarget.style, optionButtonStyle);
                }}
                disabled={loading}
              >
                <ResetIcon color={theme.colors.TEXT} /> Reset Pic
              </button>
            )}
          </div>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageChange}
          accept="image/*"
          style={{ display: 'none' }}
        />
        <input
          type="file"
          ref={cameraInputRef}
          onChange={handleImageChange}
          accept="image/*"
          capture="user"
          style={{ display: 'none' }}
        />
      </div>
      {showCamera && (
        <CameraModal
          onClose={() => setShowCamera(false)}
          onCapture={(file: File) => {
            handleFileUpload(file);
            setShowCamera(false);
          }}
        />
      )}
      {uploadImage && (
        <div style={getModalStyles()}>
          <div style={getContentStyles(theme)}>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {error && (
                <div style={{
                  backgroundColor: `${theme.colors.ERROR}20`,
                  color: theme.colors.ERROR,
                  padding: theme.spacing.MEDIUM,
                  borderRadius: '4px',
                  marginBottom: theme.spacing.MEDIUM,
                  textAlign: 'center',
                  width: '100%',
                  fontWeight: 'bold'
                }}>
                  {error}
                </div>
              )}
              <div style={{ width: '100%', position: 'relative' }}>
                <ReactCrop
                  crop={uploadCrop}
                  onChange={(_, percentCrop) => setUploadCrop(percentCrop)}
                  aspect={1}
                  circularCrop
                  minWidth={50}
                  minHeight={50}
                  ruleOfThirds
                >
                  <img
                    src={uploadImage}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      maxHeight: '60vh'
                    }}
                    alt="Upload"
                  />
                </ReactCrop>
                <div style={{
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'center',
                  marginTop: '10px'
                }}>
                  <button
                    style={{
                      padding: '10px 20px',
                      backgroundColor: theme.colors.PRIMARY,
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                    onClick={() => handleUploadCrop()}
                  >
                    Save
                  </button>
                  <button
                    style={{
                      padding: '10px 20px',
                      backgroundColor: theme.colors.ERROR,
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                    onClick={() => {
                      setUploadImage(null);
                      URL.revokeObjectURL(uploadImage);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfilePicture;
